import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocFromServer, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import Wordmark from '../components/Wordmark';
import { snoozeToDays } from '../lib/snoozeDate';

/**
 * RemindersEditPage — landing page for the "snooze or reschedule"
 * link in reminder notification emails (wishlist #4a).
 *
 * URL: /reminders/:reminderId/edit
 *
 * Flow:
 *   1. User clicks the link from their reminder email.
 *   2. If not signed in, show a Google / magic-link widget. Reuses
 *      the same pattern as AcceptInvitePage.
 *   3. Once signed in, fetch the reminder from
 *      users/{currentUid}/reminders/{reminderId}. Reminders are
 *      owner-only per rules — if the signed-in user isn't the
 *      owner, the doc won't exist for them and we show "not found".
 *   4. Render quick-action buttons (snooze 1h, snooze 1d,
 *      tomorrow 9am) plus a datetime picker for an exact time.
 *      Save flushes parsedDate + clears firedAt so firePendingReminders
 *      will fire it again at the new time.
 *   5. Mark-done sets status='done' (and removes firedAt so the
 *      reminder never fires again — until/unless it's recurring).
 *   6. Delete is below the fold for the casual user.
 *
 * No write happens until the user explicitly clicks save / mark-
 * done / delete. So a user can land here, see the wrong reminder,
 * close the tab, and nothing changes.
 */
export default function RemindersEditPage() {
  const { reminderId } = useParams();
  const navigate = useNavigate();
  const { user, signInWithGoogle, sendMagicLink } = useAuth();

  const [reminder, setReminder] = useState(undefined); // undefined=loading, null=missing
  const [errorMsg, setErrorMsg] = useState(null);
  const [actionState, setActionState] = useState('idle'); // 'idle' | 'pending' | 'saved' | 'done' | 'deleted'
  const [picked, setPicked] = useState(''); // datetime-local value
  const [magicState, setMagicState] = useState('idle');
  const [magicEmail, setMagicEmail] = useState('');

  const fetchReminder = useCallback(async (fromServer = false) => {
    if (!user?.uid || !reminderId) return;
    try {
      const ref = doc(db, 'users', user.uid, 'reminders', reminderId);
      // Walter 2026-07-04: after a snooze/reschedule the doc is rewritten by
      // the applyReminderEdit Cloud Function (Admin SDK). A plain getDoc here
      // can serve this client's CACHED copy (still the old time), so the
      // "✓ saved — next fire at …" line lied — showing the pre-snooze date
      // even though the save landed. Force a server read on the post-save
      // refresh so the confirmation reflects ground truth.
      const snap = fromServer ? await getDocFromServer(ref) : await getDoc(ref);
      if (!snap.exists()) {
        setReminder(null);
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      setReminder(data);
      // Pre-fill the datetime picker from the current parsedDate so
      // the user can tweak rather than re-enter from scratch.
      const ms = data.parsedDate?.toMillis?.() || data.parsedDate?._seconds * 1000 || null;
      if (ms) {
        // datetime-local needs YYYY-MM-DDTHH:MM in LOCAL time.
        const d = new Date(ms);
        const pad = n => String(n).padStart(2, '0');
        const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        setPicked(local);
      }
    } catch (err) {
      console.warn('[RemindersEdit] fetch failed', err);
      setErrorMsg(err?.message || 'failed to load reminder');
    }
  }, [user?.uid, reminderId]);

  useEffect(() => { fetchReminder(); }, [fetchReminder]);

  const persistDate = async (dateMs) => {
    if (!user?.uid || !reminderId) return;
    setActionState('pending');
    setErrorMsg(null);
    try {
      // Walter 2026-05-15: route through the Cloud Function
      // applyReminderEdit so the source list's bell-line gets
      // rewritten too. The previous direct updateDoc only patched
      // the reminder doc — agenda + popover showed the new time,
      // but the source list still showed the original line.
      const fn = httpsCallable(functions, 'applyReminderEdit');
      await fn({ reminderId, parsedDateMs: dateMs });
      // Re-read from the SERVER before confirming, so "✓ saved — next fire
      // at …" shows the value that actually landed (not a stale cache).
      await fetchReminder(true);
      setActionState('saved');
    } catch (err) {
      console.error('[RemindersEdit] save failed', err);
      setErrorMsg(err?.message || 'save failed');
      setActionState('idle');
    }
  };

  // Walter 2026-05-15: +1 hour / +3 hours snooze from NOW (the user
  // is explicitly delaying by that many hours from "this moment").
  // Walter 2026-07-19: +1 day / +1 week now use THE one snooze rule
  // (lib/snoozeDate): TODAY + N days at the reminder's original
  // time-of-day — never original+N (an overdue reminder must land in
  // the future). Tomorrow-9am stays explicit (sets time to 9am).
  const onSnoozeFromNow = (msFromNow) => {
    persistDate(Date.now() + msFromNow);
  };
  const onSnoozeDays = (days) => {
    const baseMs = reminder?.parsedDate?.toMillis?.()
      || (reminder?.parsedDate?._seconds ? reminder.parsedDate._seconds * 1000 : null)
      || null;
    const next = snoozeToDays(baseMs ? new Date(baseMs) : null, days);
    persistDate(next.getTime());
  };

  const onTomorrow9am = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    persistDate(d.getTime());
  };

  const onSavePicked = () => {
    if (!picked) return;
    const d = new Date(picked);
    if (isNaN(d.getTime())) {
      setErrorMsg('invalid date');
      return;
    }
    persistDate(d.getTime());
  };

  const onMarkDone = async () => {
    if (!user?.uid || !reminderId) return;
    setActionState('pending');
    setErrorMsg(null);
    try {
      await updateDoc(doc(db, 'users', user.uid, 'reminders', reminderId), {
        status: 'done',
        firedAt: null,
        updatedAt: serverTimestamp(),
      });
      setActionState('done');
    } catch (err) {
      setErrorMsg(err?.message || 'failed to mark done');
      setActionState('idle');
    }
  };

  const onDelete = async () => {
    if (!user?.uid || !reminderId) return;
    if (!window.confirm('Delete this reminder? This cannot be undone.')) return;
    setActionState('pending');
    setErrorMsg(null);
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'reminders', reminderId));
      setActionState('deleted');
    } catch (err) {
      setErrorMsg(err?.message || 'failed to delete');
      setActionState('idle');
    }
  };

  const onMagicSubmit = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(magicEmail.trim())) {
      setErrorMsg('please enter a valid email');
      return;
    }
    setErrorMsg(null);
    setMagicState('sending');
    try {
      await sendMagicLink(
        magicEmail.trim(),
        window.location.origin + window.location.pathname,
      );
      setMagicState('sent');
    } catch (err) {
      setErrorMsg('could not send the link. try Google sign-in instead.');
      setMagicState('idle');
    }
  };

  return (
    <div style={pageWrap}>
      <div style={card}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
          <Wordmark fontSize={26} scaleWithUi={false} />
        </div>

        {!user && (
          <SignInBody
            onGoogle={signInWithGoogle}
            onMagicSubmit={onMagicSubmit}
            magicState={magicState}
            magicEmail={magicEmail}
            setMagicEmail={setMagicEmail}
            errorMsg={errorMsg}
            onMagicReset={() => { setMagicState('idle'); setMagicEmail(''); }}
          />
        )}

        {user && reminder === undefined && (
          <Status>loading…</Status>
        )}

        {user && reminder === null && (
          <>
            <Status>reminder not found</Status>
            <p style={hint}>
              the link may have expired, or this reminder belongs to a different account.
              you're signed in as <span style={{ color: 'var(--text)' }}>{user.email}</span>.
            </p>
            <ExitButton onClick={() => navigate('/', { replace: true })} />
          </>
        )}

        {user && reminder && actionState === 'deleted' && (
          <>
            <Status tone="ok">✓ deleted</Status>
            <ExitButton onClick={() => navigate('/', { replace: true })} />
          </>
        )}

        {user && reminder && actionState === 'done' && (
          <>
            <Status tone="ok">✓ marked done</Status>
            <ExitButton onClick={() => navigate('/', { replace: true })} />
          </>
        )}

        {user && reminder && actionState !== 'deleted' && actionState !== 'done' && (
          <>
            <Title>snooze or reschedule</Title>
            <div style={reminderText}>
              🔔 {reminder.text || '(no text)'}
            </div>
            {reminder.parsedDate && (
              <div style={hint}>
                currently set for {fmtAbsolute(reminder.parsedDate)}
              </div>
            )}
            {actionState === 'saved' && (
              <div style={{ ...hint, color: 'var(--accent)' }}>
                ✓ saved — next fire at {fmtAbsolute(reminder.parsedDate)}
              </div>
            )}

            <div style={{ marginTop: 18, marginBottom: 8, fontSize: 11, color: 'var(--text-dimmer)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              quick snooze
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <QuickButton onClick={() => onSnoozeFromNow(60 * 60 * 1000)} disabled={actionState === 'pending'}>+1 hour</QuickButton>
              <QuickButton onClick={() => onSnoozeFromNow(3 * 60 * 60 * 1000)} disabled={actionState === 'pending'}>+3 hours</QuickButton>
              <QuickButton onClick={() => onSnoozeDays(1)} disabled={actionState === 'pending'}>+1 day</QuickButton>
              <QuickButton onClick={onTomorrow9am} disabled={actionState === 'pending'}>tomorrow 9am</QuickButton>
              <QuickButton onClick={() => onSnoozeDays(7)} disabled={actionState === 'pending'}>+1 week</QuickButton>
            </div>

            <div style={{ marginTop: 22, marginBottom: 8, fontSize: 11, color: 'var(--text-dimmer)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              or pick exact time
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="datetime-local"
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
                disabled={actionState === 'pending'}
                style={{
                  flex: 1,
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  fontFamily: 'var(--mono)',
                  fontSize: '13px',
                  padding: '8px 10px',
                  borderRadius: 'var(--radius)',
                  outline: 'none',
                }}
              />
              <button
                onClick={onSavePicked}
                disabled={actionState === 'pending' || !picked}
                style={primaryButton}
              >
                {actionState === 'pending' ? '…' : 'save'}
              </button>
            </div>

            <div style={{
              marginTop: 24, paddingTop: 18,
              borderTop: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
            }}>
              <button
                onClick={onMarkDone}
                disabled={actionState === 'pending'}
                style={secondaryButton}
              >
                ✓ mark done
              </button>
              <button
                onClick={onDelete}
                disabled={actionState === 'pending'}
                style={dangerButton}
              >
                delete
              </button>
            </div>

            {errorMsg && (
              <div style={{
                marginTop: 14,
                padding: '8px 10px',
                background: 'var(--surface2)',
                border: '1px solid var(--danger, #d04a4a)',
                borderRadius: 'var(--radius)',
                fontSize: 12,
                color: 'var(--danger, #d04a4a)',
              }}>
                {errorMsg}
              </div>
            )}

            {/* Nav escape hatch — without this, the page is a dead-end
                after a reminder click-through. The two options the user
                might want: jump to the full reminders surface (bell icon
                inside the app) or just go to their boards. Both routes
                land at /, which boots into the user's default board
                where the 🔔 in the header is one click away. */}
            <div style={{
              marginTop: 18, paddingTop: 14,
              borderTop: '1px dashed var(--border)',
              display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap',
              fontSize: 11, color: 'var(--text-dimmer)',
            }}>
              <button
                onClick={() => navigate('/', { replace: false })}
                style={navLink}
              >
                ← my boards
              </button>
              <span style={{ color: 'var(--border-bright)' }}>·</span>
              <button
                onClick={() => navigate('/', { replace: false })}
                style={navLink}
                title="opens listicity; click the 🔔 in the top header to see all reminders"
              >
                all reminders 🔔
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const navLink = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-dim)',
  fontFamily: 'var(--mono)',
  fontSize: 11,
  cursor: 'pointer',
  padding: '4px 8px',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
};

// ─── helpers ───────────────────────────────────────────────────────

function SignInBody({ onGoogle, onMagicSubmit, magicState, magicEmail, setMagicEmail, errorMsg, onMagicReset }) {
  if (magicState === 'sent') {
    return (
      <>
        <Status tone="ok">✓ check your inbox</Status>
        <p style={hint}>
          we sent a sign-in link to <span style={{ color: 'var(--text)' }}>{magicEmail}</span>.
          click it from this device — you'll come back to this page and the reminder will load.
        </p>
        <button onClick={onMagicReset} style={{ ...secondaryButton, marginTop: 12 }}>
          use a different option
        </button>
      </>
    );
  }
  return (
    <>
      <Title>sign in to edit this reminder</Title>
      <p style={hint}>
        reminders are private to your account, so we need to confirm it's you.
      </p>
      <div style={{ marginTop: 16 }}>
        <button onClick={onGoogle} style={{ ...primaryButton, width: '100%' }}>
          Sign in with Google
        </button>
      </div>
      <div style={{
        margin: '14px 0', display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 12, color: 'var(--text-dimmer)', textTransform: 'uppercase', letterSpacing: '1px',
      }}>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        or with email
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>
      <form onSubmit={onMagicSubmit}>
        <input
          type="email"
          value={magicEmail}
          onChange={e => setMagicEmail(e.target.value)}
          placeholder="your@email.com"
          autoComplete="email"
          disabled={magicState === 'sending'}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '11px 14px',
            background: 'var(--bg)',
            border: '1px solid var(--border-bright)',
            borderRadius: 'var(--radius)',
            color: 'var(--text)',
            fontFamily: 'var(--mono)',
            fontSize: 14,
            outline: 'none',
            marginBottom: 8,
          }}
        />
        <button type="submit" disabled={magicState === 'sending'} style={{ ...secondaryButton, width: '100%' }}>
          {magicState === 'sending' ? 'sending…' : 'Email me a sign-in link'}
        </button>
      </form>
      {errorMsg && (
        <div style={{ marginTop: 10, color: 'var(--danger)', fontFamily: 'var(--mono)', fontSize: 12 }}>
          {errorMsg}
        </div>
      )}
    </>
  );
}

function ExitButton({ onClick }) {
  return (
    <div style={{ marginTop: 18, textAlign: 'center' }}>
      <button onClick={onClick} style={secondaryButton}>
        go to listicity
      </button>
    </div>
  );
}

function Title({ children }) {
  return (
    <div style={{
      fontFamily: 'var(--mono)', fontWeight: 600,
      fontSize: 16, color: 'var(--text)',
      marginBottom: 8, textAlign: 'center',
    }}>
      {children}
    </div>
  );
}

function Status({ tone, children }) {
  const color = tone === 'ok' ? 'var(--accent)'
    : tone === 'err' ? 'var(--danger, #d04a4a)'
    : 'var(--text)';
  return (
    <div style={{
      fontFamily: 'var(--mono)', fontWeight: 600,
      fontSize: 16, color, marginBottom: 8, textAlign: 'center',
    }}>
      {children}
    </div>
  );
}

function QuickButton({ onClick, disabled, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'var(--surface2)',
        border: '1px solid var(--border-bright)',
        color: 'var(--text)',
        padding: '8px 12px',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
        fontSize: 12,
        cursor: disabled ? 'wait' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function fmtAbsolute(v) {
  if (!v) return '—';
  const ms = typeof v.toMillis === 'function'
    ? v.toMillis()
    : (typeof v._seconds === 'number' ? v._seconds * 1000 : (typeof v === 'number' ? v : null));
  if (!ms) return '—';
  return new Date(ms).toLocaleString();
}

const pageWrap = {
  minHeight: '100vh',
  background: 'var(--bg)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
};

const card = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: '36px 32px 28px',
  maxWidth: 460,
  width: '100%',
  fontFamily: 'var(--mono)',
};

const hint = {
  fontFamily: 'var(--mono)',
  fontSize: 12,
  color: 'var(--text-dim)',
  marginTop: 8,
  textAlign: 'center',
};

const reminderText = {
  fontFamily: 'var(--mono)',
  fontSize: 15,
  color: 'var(--text)',
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: '12px 14px',
  marginTop: 12,
  textAlign: 'left',
};

const primaryButton = {
  padding: '8px 16px',
  background: 'var(--accent)',
  color: 'var(--bg)',
  border: '1px solid var(--accent)',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--mono)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButton = {
  padding: '8px 16px',
  background: 'transparent',
  color: 'var(--text-dim)',
  border: '1px solid var(--border-bright)',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--mono)',
  fontSize: 12,
  cursor: 'pointer',
};

const dangerButton = {
  padding: '8px 14px',
  background: 'transparent',
  color: 'var(--danger, #d04a4a)',
  border: '1px solid var(--danger, #d04a4a)',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--mono)',
  fontSize: 12,
  cursor: 'pointer',
};
